import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertCircle, TrendingUp } from 'lucide-react';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { getBackendUrl } from '../../utils/api';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

export const MasterAIUsageContent = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [onlyEmergent, setOnlyEmergent] = useState(false);
  const [expanded, setExpanded] = useState({});
  const monthOptions = React.useMemo(() => {
    const opts = []; const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
      opts.push({ val, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return opts;
  }, []);
  useEffect(() => { fetchUsage(); /* eslint-disable-next-line */ }, [period, onlyEmergent]);
  const fetchUsage = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/master/ai-usage?period=${encodeURIComponent(period)}&only_emergent=${onlyEmergent}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data); setLoading(false);
    } catch (error) {
      console.error('Error fetching AI usage:', error);
      toast.error('Erro ao carregar consumo de IA'); setLoading(false);
    }
  };
  const nf = (n) => Number(n || 0).toLocaleString('pt-BR');
  const usd = (n) => `US$ ${Number(n || 0).toFixed(4)}`;
  const totals = data?.totals || {};
  const stores = data?.by_store || [];
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Consumo de IA</h1>
          <p className="text-gray-400 mt-1">Quanto cada lojista consome da IA (chave interna Emergent)</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-gray-300 bg-white/5 px-3 py-2 rounded-lg">
            <Switch checked={onlyEmergent} onCheckedChange={setOnlyEmergent} />
            Só chave Emergent
          </label>
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className="glass-input">
            {monthOptions.map((o) => (<option key={o.val} value={o.val}>{o.label}</option>))}
            <option value="7d">Últimos 7 dias</option>
            <option value="30d">Últimos 30 dias</option>
            <option value="all">Todo o período</option>
          </select>
        </div>
      </div>
      <div className="glass-card border border-amber-500/30 bg-amber-500/5">
        <div className="flex items-start gap-3">
          <AlertCircle size={20} className="text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-200/90">
            Os tokens são contados de forma aproximada e o <strong>custo é uma estimativa</strong> baseada nos preços
            públicos de cada modelo — serve para comparar quem consome mais, não é a fatura exata do Emergent.
          </p>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="loading-spinner"></div></div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="glass-card"><p className="text-gray-400 text-sm">Requisições de IA</p><p className="text-2xl font-bold text-white mt-1">{nf(totals.requests)}</p></div>
            <div className="glass-card"><p className="text-gray-400 text-sm">Tokens totais</p><p className="text-2xl font-bold text-white mt-1">{nf(totals.total_tokens)}</p><p className="text-xs text-gray-500 mt-1">entrada {nf(totals.input_tokens)} · saída {nf(totals.output_tokens)}</p></div>
            <div className="glass-card"><p className="text-gray-400 text-sm">Custo estimado</p><p className="text-2xl font-bold text-emerald-400 mt-1">{usd(totals.estimated_cost_usd)}</p></div>
            <div className="glass-card"><p className="text-gray-400 text-sm">Lojistas com uso</p><p className="text-2xl font-bold text-white mt-1">{nf(stores.length)}</p></div>
          </div>
          <div className="glass-card">
            <h2 className="text-xl font-semibold text-white mb-4">Consumo por Lojista</h2>
            {stores.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <TrendingUp size={40} className="mx-auto mb-3 opacity-40" />
                <p>Nenhum consumo de IA registrado neste período.</p>
                <p className="text-sm text-gray-500 mt-1">Os dados aparecem conforme os lojistas usam a IA.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-gray-400 text-sm border-b border-white/10">
                      <th className="py-3 pr-4 font-medium">Lojista</th>
                      <th className="py-3 px-4 font-medium text-right">Requisições</th>
                      <th className="py-3 px-4 font-medium text-right">Tokens</th>
                      <th className="py-3 px-4 font-medium text-right">Custo est.</th>
                      <th className="py-3 pl-4 font-medium text-right">Detalhes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stores.map((s) => (
                      <React.Fragment key={s.tenant_id}>
                        <tr className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-3 pr-4 text-white font-medium">{s.store_name}</td>
                          <td className="py-3 px-4 text-right text-gray-200">{nf(s.requests)}</td>
                          <td className="py-3 px-4 text-right text-gray-200">{nf(s.total_tokens)}<span className="block text-xs text-gray-500">in {nf(s.input_tokens)} · out {nf(s.output_tokens)}</span></td>
                          <td className="py-3 px-4 text-right text-emerald-400 font-medium">{usd(s.estimated_cost_usd)}</td>
                          <td className="py-3 pl-4 text-right">
                            <button onClick={() => setExpanded((prev) => ({ ...prev, [s.tenant_id]: !prev[s.tenant_id] }))} className="text-purple-400 hover:text-purple-300 text-sm">
                              {expanded[s.tenant_id] ? 'Ocultar' : 'Ver por recurso'}
                            </button>
                          </td>
                        </tr>
                        {expanded[s.tenant_id] && (
                          <tr className="bg-black/20">
                            <td colSpan={5} className="py-3 px-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {(s.by_feature || []).map((f) => (
                                  <div key={f.feature} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
                                    <span className="text-gray-200 text-sm">{f.label}</span>
                                    <span className="text-gray-400 text-xs">{nf(f.requests)} req · {nf(f.total_tokens)} tokens · {usd(f.estimated_cost_usd)}</span>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

